import { type Audit } from '#packages/data-collection/models/audit';
import { type AuditBackgroundForm } from '#packages/data-collection/models/audit-background-form';
import { WEEK_DAY_SHORT_NAMES } from '../../audit-form-background/constants';

import { type ExcelSheetMapping } from '../types/excel-mapping';

/**
 * Source data for the Background sheet export.
 * Combines audit-level information with the background form data.
 */
export type BackgroundExportData = {
  audit: Audit;
  form: AuditBackgroundForm;
};

/**
 * Maps Background form fields to Excel named ranges in the "1. Background" sheet.
 *
 * Range names are defined in the Excel template's Name Manager.
 * Fields left unmapped (value returns undefined) are intentionally skipped
 * and may be mapped in a future iteration.
 */
export const backgroundSheetMapping: ExcelSheetMapping<BackgroundExportData> = {
  label: 'Background',

  fields: [
    /* ── Audit-level ─────────────────────────────────────────── */
    {
      rangeName: 'background_audit_id',
      getValue: (d) => d.audit.serverAuditId,
    },

    /* ── General: Organisation / Building ────────────────────── */
    {
      rangeName: 'background_client_organization',
      getValue: (d) => d.form.general.organizationName,
    },
    {
      rangeName: 'background_building_name',
      getValue: (d) => d.form.general.buildingName,
    },

    /* ── General: Address ────────────────────────────────────── */
    {
      rangeName: 'background_street_address',
      getValue: (d) => d.form.general.address?.addressLine1,
    },
    {
      rangeName: 'background_city',
      getValue: (d) => d.form.general.address?.city,
    },
    {
      rangeName: 'background_province',
      getValue: (d) => d.form.general.address?.state,
    },
    {
      rangeName: 'background_postal_code',
      getValue: (d) => d.form.general.address?.postalCode,
    },

    /* ── General: Site Contacts (max 2) ──────────────────────── */
    {
      rangeName: 'background_site_contact_1_name',
      getValue: (d) => d.form.general.siteContacts?.[0]?.name,
    },
    {
      rangeName: 'background_site_contact_1_role',
      getValue: (d) => d.form.general.siteContacts?.[0]?.role,
    },
    {
      rangeName: 'background_site_contact_1_email',
      getValue: (d) => d.form.general.siteContacts?.[0]?.email,
    },
    {
      rangeName: 'background_site_contact_1_phone',
      getValue: (d) => d.form.general.siteContacts?.[0]?.phone,
    },
    {
      rangeName: 'background_site_contact_1_notes',
      getValue: (d) => d.form.general.siteContacts?.[0]?.notes,
    },
    {
      rangeName: 'background_site_contact_2_name',
      getValue: (d) => d.form.general.siteContacts?.[1]?.name,
    },
    {
      rangeName: 'background_site_contact_2_role',
      getValue: (d) => d.form.general.siteContacts?.[1]?.role,
    },
    {
      rangeName: 'background_site_contact_2_email',
      getValue: (d) => d.form.general.siteContacts?.[1]?.email,
    },
    {
      rangeName: 'background_site_contact_2_phone',
      getValue: (d) => d.form.general.siteContacts?.[1]?.phone,
    },
    {
      rangeName: 'background_site_contact_2_notes',
      getValue: (d) => d.form.general.siteContacts?.[1]?.notes,
    },

    /* ── General: Auditor Team (max 2) ───────────────────────── */
    {
      rangeName: 'background_auditor_name_1',
      getValue: (d) => d.form.general.auditorTeam?.[0]?.name,
    },
    {
      rangeName: 'background_auditor_name_2',
      getValue: (d) => d.form.general.auditorTeam?.[1]?.name,
    },

    /* ── General: Visit Dates & Times ────────────────────────── */
    {
      rangeName: 'background_visit_start_date',
      getValue: (d) => d.form.general.siteVisitStartDate?.format('YYYY-MM-DD'),
    },
    {
      rangeName: 'background_visit_start_time',
      getValue: (d) => d.form.general.siteVisitStartDate?.format('HH:mm'),
    },
    {
      rangeName: 'background_visit_end_date',
      getValue: (d) => d.form.general.siteVisitEndDate?.format('YYYY-MM-DD'),
    },
    {
      rangeName: 'background_visit_end_time',
      getValue: (d) => d.form.general.siteVisitEndDate?.format('HH:mm'),
    },

    /* ── General: Weather ────────────────────────────────────── */
    {
      rangeName: 'background_weather_conditions_start',
      getValue: (d) => d.form.general.weatherAtStart,
    },
    {
      rangeName: 'background_weather_conditions_end',
      getValue: (d) => d.form.general.weatherAtEnd,
    },

    /* ── General: Uninsulated Piping ─────────────────────────── */
    {
      rangeName: 'background_in_merch_rm',
      getValue: (d) => d.form.general.heatingHotWater,
    },

    /* ── General: Construction ───────────────────────────────── */
    {
      rangeName: 'background_construction_year',
      getValue: (d) => d.form.general.yearBuilt,
    },
    {
      rangeName: 'background_recent_upgrades',
      getValue: (d) => d.form.general.recentUpgrades,
    },
    {
      rangeName: 'background_plans_for_upgrades',
      getValue: (d) => d.form.general.plansForUpgrades,
    },

    /* ── General: Building Dimensions ────────────────────────── */
    {
      rangeName: 'background_gross_floor_area',
      getValue: (d) => d.form.general.grossFloorArea,
    },
    {
      rangeName: 'background_above_ground_stories',
      getValue: (d) => d.form.general.floors,
    },
    {
      rangeName: 'background_parkade_levels',
      getValue: (d) => d.form.general.parkadeLevels,
    },
    {
      rangeName: 'background_other_floors',
      getValue: (d) => d.form.general.otherFloors,
    },

    /* ── General: Operation ──────────────────────────────────── */
    {
      rangeName: 'background_hours_of_operation',
      getValue: (d) => {
        const schedule = d.form.general.operationSchedule;
        if (!schedule?.length) return undefined;
        return [...schedule]
          .sort((a, b) => a.weekDay - b.weekDay)
          .map((entry) =>
            entry.isUnavailable
              ? `${WEEK_DAY_SHORT_NAMES[entry.weekDay]}: Unavailable`
              : `${WEEK_DAY_SHORT_NAMES[entry.weekDay]}: ${entry.startTime ?? '?'}–${entry.endTime ?? '?'}`,
          )
          .join(', ');
      },
    },
    {
      rangeName: 'background_description_of_space_uses',
      getValue: (d) => d.form.general.spaceUsesDescription,
    },
    {
      rangeName: 'background_descriptions_of_occupants',
      getValue: (d) => d.form.general.occupantsDescription,
    },

    /* ── General: MURB Suites (max 4 types) ──────────────────── */
    {
      rangeName: 'background_murb_suites_type_1',
      getValue: (d) => d.form.general.murbSuites?.[0]?.num,
    },
    {
      rangeName: 'background_murb_suites_type_2',
      getValue: (d) => d.form.general.murbSuites?.[1]?.num,
    },
    {
      rangeName: 'background_murb_suites_type_3',
      getValue: (d) => d.form.general.murbSuites?.[2]?.num,
    },
    {
      rangeName: 'background_murb_suites_type_4',
      getValue: (d) => d.form.general.murbSuites?.[3]?.num,
    },

    /* ── General: Occupants ──────────────────────────────────── */
    {
      rangeName: 'background_description_of_occupants',
      getValue: (d) => d.form.general.occupants?.description,
    },
    {
      rangeName: 'background_occupants_full_time_employees',
      getValue: (d) => d.form.general.occupants?.fullTimeEmployees,
    },
    {
      rangeName: 'background_occupants_visitors',
      getValue: (d) => d.form.general.occupants?.visitors,
    },
    {
      rangeName: 'background_occupants_retail_customers',
      getValue: (d) => d.form.general.occupants?.retailCustomers,
    },
    {
      rangeName: 'background_occupants_students',
      getValue: (d) => d.form.general.occupants?.k12Students,
    },
    {
      rangeName: 'background_occupants_residential_tenants',
      getValue: (d) => d.form.general.occupants?.residentialTenants,
    },

    /* ── General: Meters (count per type) ────────────────────── */
    {
      rangeName: 'background_meters_electricity',
      getValue: (d) =>
        d.form.general.meters?.filter((m) => m.type === 'electricity').length ??
        0,
    },
    {
      rangeName: 'background_meters_natural_gas',
      getValue: (d) =>
        d.form.general.meters?.filter((m) => m.type === 'natural_gas').length ??
        0,
    },
    {
      rangeName: 'background_meters_water',
      getValue: (d) =>
        d.form.general.meters?.filter((m) => m.type === 'water').length ?? 0,
    },
    {
      rangeName: 'background_meters_other',
      getValue: (d) =>
        d.form.general.meters?.filter((m) => m.type === 'other').length ?? 0,
    },

    /* ── General: Elevators / Car Outlets ────────────────────── */
    {
      rangeName: 'background_elevators',
      getValue: (d) => d.form.general.elevators,
    },
    {
      rangeName: 'background_exterior_car_outlets',
      getValue: (d) => d.form.general.exteriorCarOutlets,
    },

    /* ── General: EV Chargers ────────────────────────────────── */
    {
      rangeName: 'background_ev_chargers',
      getValue: (d) => d.form.general.evChargers?.count,
    },

    /* ── Envelope ────────────────────────────────────────────── */
    {
      rangeName: 'background_window_type',
      getValue: (d) =>
        d.form.envelope.windowTypes
          ?.map((w) =>
            [w.material, w.numberOfPanes ? `${w.numberOfPanes}-pane` : null]
              .filter(Boolean)
              .join(', '),
          )
          .filter(Boolean)
          .join('; ') || undefined,
    },
    {
      rangeName: 'background_door_type',
      getValue: (d) =>
        d.form.envelope.doorTypes
          ?.map((door) =>
            [door.materials, door.types].filter(Boolean).join(', '),
          )
          .filter(Boolean)
          .join('; ') || undefined,
    },
    {
      rangeName: 'background_wall_type',
      getValue: (d) =>
        d.form.envelope.wallTypes
          ?.map((wall) =>
            [wall.claddingTypes, wall.structuralFraming]
              .filter(Boolean)
              .join(', '),
          )
          .filter(Boolean)
          .join('; ') || undefined,
    },
    {
      rangeName: 'background_roofing_type',
      getValue: (d) =>
        d.form.envelope.roofingTypes
          ?.map((r) => r.types)
          .filter(Boolean)
          .join('; ') || undefined,
    },

    /* ── Controls ────────────────────────────────────────────── */
    {
      rangeName: 'background_bas',
      getValue: (d) => d.form.controls.buildingAutomationSystem,
    },

    /* ── Space Heating ───────────────────────────────────────── */
    {
      rangeName: 'background_space_heating_primary_system',
      getValue: (d) => d.form.spaceHeating.systems?.[0],
    },
    {
      rangeName: 'background_space_heating_secondary_system',
      getValue: (d) => d.form.spaceHeating.systems?.[1],
    },

    /* ── Space Cooling ───────────────────────────────────────── */
    {
      rangeName: 'background_space_cooling_primary_system',
      getValue: (d) => d.form.spaceCooling.systems?.[0],
    },
    {
      rangeName: 'background_spaec_cooling_secondary_system',
      getValue: (d) => d.form.spaceCooling.systems?.[1],
    },

    /* ── Ventilation ─────────────────────────────────────────── */
    {
      rangeName: 'background_ventilation_system',
      getValue: (d) => d.form.ventilation.system,
    },
    {
      rangeName: 'background_ventilation_variable_air_volume',
      getValue: (d) => d.form.ventilation.isVariableAirVolumeSystem,
    },
    {
      rangeName: 'background_ventilation_building_sensors',
      getValue: (d) => d.form.ventilation.hasCO2Sensors,
    },
    {
      rangeName: 'background_ventilation_parkade_sensors',
      getValue: (d) => d.form.ventilation.hasParkadeCOSensors,
    },

    /* ── Domestic Cold Water ─────────────────────────────────── */
    {
      rangeName: 'background_dcw_other_water_loads',
      getValue: (d) => d.form.domesticColdWater.otherWaterLoads,
    },
    {
      rangeName: 'background_dcw_pipe_leaks',
      getValue: (d) => d.form.domesticColdWater.hadPipeLeaks,
    },

    /* ── Domestic Hot Water ──────────────────────────────────── */
    {
      rangeName: 'background_dhw_system',
      getValue: (d) => d.form.domesticHotWater.system,
    },

    /* ── Lighting ────────────────────────────────────────────── */
    {
      rangeName: 'background_lighting_exterior_lighting_controls',
      getValue: (d) => d.form.lighting.exteriorLightingControls,
    },

    /* ── Other ───────────────────────────────────────────────── */
    {
      rangeName: 'background_rooms_not_visited',
      getValue: (d) => d.form.other.unvisitedRooms,
    },
    {
      rangeName: 'background_other',
      getValue: (d) => d.form.other.extraDescription,
    },

    /* ── Electrical: Main Breaker ────────────────────────────── */
    {
      rangeName: 'background_main_breaker_amperage',
      getValue: (d) => d.form.mainBreaker.amperage,
    },
    {
      rangeName: 'background_main_breaker_voltage',
      getValue: (d) => d.form.mainBreaker.voltage,
    },
    {
      rangeName: 'background_main_breaker_located',
      getValue: (d) => d.form.mainBreaker.location,
    },

    /* ── Ranges that are intentionally left unmapped for now ── */
    {
      rangeName: 'background_sections_added',
      getValue: () => undefined,
    },
    {
      rangeName: 'background_no_go_projects',
      getValue: (d) => d.form.general.noGoProjects,
    },
    {
      rangeName: 'background_energy_opportunities',
      getValue: (d) => d.form.general.energyOpportunities,
    },
    {
      rangeName: 'background_uninsulated_piping',
      getValue: () => undefined,
    },
    {
      rangeName: 'background_heat_recovery',
      getValue: () => undefined,
    },
    {
      rangeName: 'background_space_heating_heat_recovery',
      getValue: (d) => d.form.spaceHeating.hasRecoverySystem,
    },
  ],
};
